import { Icon } from '@chakra-ui/react';
import {
  Box,
  Button,
  ChakraSelect,
  Divider,
  FileUploader,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UploadedFile,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import {
  useDownloadDealsSheetApi,
  useDownloadSupplierProductsSheetApi,
  useUploadDealsSheetApi,
  useUploadSupplierProductsSheetApi,
} from 'apis/exclusive-deals';
import { useSearchSuppliersApi } from 'apis/supplier';
import { cloneElement, useMemo, useState } from 'react';
import { FiDownload, FiUpload } from 'react-icons/fi';

interface SupplierOption {
  value: number;
  label: string;
}

interface Props {
  trigger: JSX.Element;
}

const EXCEL_MIME_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

/**
 * Combined Import / Export modal for the exclusive-deals domain.
 * One supplier picker drives two tabs:
 *  - Exclusive deals: supplier optional for download (omit → all deals), required for upload.
 *  - Supplier catalog: supplier required for both (path param) — manages products + images.
 */
export const DealsSheetModal = ({ trigger }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [search, setSearch] = useState('');
  const [dealsFile, setDealsFile] = useState<UploadedFile>();
  const [catalogFile, setCatalogFile] = useState<UploadedFile>();

  const supplierId = supplier?.value ?? null;

  const { data: suppliersData, isLoading: isSuppliersLoading } = useSearchSuppliersApi(search, {
    enabled: search.length >= 2,
  });
  const supplierOptions = useMemo<SupplierOption[]>(
    () => (suppliersData?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [suppliersData]
  );

  // Deals sheet
  const { refetch: downloadDeals, isFetching: isDownloadingDeals } = useDownloadDealsSheetApi(supplierId);
  const {
    mutate: uploadDeals,
    isLoading: isUploadingDeals,
    error: dealsUploadError,
    data: dealsUploadResult,
    reset: resetDealsUpload,
  } = useUploadDealsSheetApi();
  const dealsRowErrors = dealsUploadError?.response?.data?.errors ?? [];

  // Supplier catalog
  const { refetch: downloadCatalog, isFetching: isDownloadingCatalog } =
    useDownloadSupplierProductsSheetApi(supplierId);
  const {
    mutate: uploadCatalog,
    isLoading: isUploadingCatalog,
    error: catalogUploadError,
    data: catalogUploadResult,
    reset: resetCatalogUpload,
  } = useUploadSupplierProductsSheetApi();
  const catalogRowErrors = catalogUploadError?.response?.data?.errors ?? [];

  const onUploadDeals = () => {
    if (!supplier || !dealsFile) return;
    const formData = new FormData();
    formData.append('file', dealsFile as File);
    uploadDeals({ supplierId: supplier.value, body: formData });
  };

  const onUploadCatalog = () => {
    if (!supplier || !catalogFile) return;
    const formData = new FormData();
    formData.append('file', catalogFile as File);
    uploadCatalog({ supplierId: supplier.value, body: formData });
  };

  const onCloseModal = () => {
    onClose();
    setSupplier(null);
    setSearch('');
    setDealsFile(undefined);
    setCatalogFile(undefined);
    resetDealsUpload();
    resetCatalogUpload();
  };

  return (
    <>
      {cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} size='xl' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Import / Export Sheets
            <Text fontSize='sm' fontWeight='normal' color='gray.500' mt='1'>
              Download a sheet, edit it, and upload it back to apply changes.
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack align='stretch' spacing='lg'>
              {/* Shared supplier picker */}
              <FormControl>
                <FormLabel>Supplier</FormLabel>
                <ChakraSelect
                  options={supplierOptions}
                  value={supplier}
                  isLoading={isSuppliersLoading}
                  isClearable
                  placeholder='Search supplier by name...'
                  onInputChange={(input: string) => setSearch(input)}
                  filterOption={() => true}
                  onChange={(opt: any) => setSupplier(opt ?? null)}
                  noOptionsMessage={() =>
                    search.length < 2 ? 'Type at least 2 characters to search' : 'No suppliers found'
                  }
                />
                <Text fontSize='xs' color='gray.500' mt='1'>
                  Optional for deals download · Required for uploads & catalog
                </Text>
              </FormControl>

              <Tabs colorScheme='red' variant='enclosed' isLazy>
                <TabList>
                  <Tab>Exclusive deals</Tab>
                  <Tab>Supplier catalog</Tab>
                </TabList>

                <TabPanels>
                  {/* ---------- Exclusive deals ---------- */}
                  <TabPanel px='0'>
                    <VStack align='stretch' spacing='lg'>
                      {/* Download */}
                      <VStack align='stretch' spacing='sm'>
                        <Text fontWeight='semibold'>Download deals</Text>
                        <Text fontSize='sm' color='gray.500'>
                          {supplier
                            ? `Export ${supplier.label}'s catalog with active deals.`
                            : 'Export all deals, matching the list filters.'}
                        </Text>
                        <Box>
                          <Button
                            size='sm'
                            variant='outline'
                            colorScheme='red'
                            leftIcon={<Icon as={FiDownload} />}
                            isLoading={isDownloadingDeals}
                            onClick={() => downloadDeals()}>
                            Download deals sheet
                          </Button>
                        </Box>
                      </VStack>

                      <Divider />

                      {/* Upload */}
                      <VStack align='stretch' spacing='sm'>
                        <Text fontWeight='semibold'>Upload deals</Text>
                        <Text fontSize='sm' color='gray.500'>
                          Create or update deals (.xls/.xlsx, max 2&nbsp;MB). A Deal ID updates; a blank one creates.
                          All-or-nothing.
                        </Text>
                        {!supplier && (
                          <Text fontSize='sm' color='orange.500'>
                            Select a supplier above to enable upload.
                          </Text>
                        )}

                        <FileUploader
                          style='style_01'
                          acceptedTypes={[...EXCEL_MIME_TYPES]}
                          multiple={false}
                          onRemove={() => setDealsFile(undefined)}
                          onRemoveAll={() => setDealsFile(undefined)}
                          onUpload={(files: UploadedFile[]) => {
                            setDealsFile(files[0]);
                            resetDealsUpload();
                          }}
                        />

                        {dealsUploadResult && (
                          <Box bg='green.50' borderRadius='md' p='sm'>
                            <Text fontSize='sm' color='green.700'>
                              Uploaded: {dealsUploadResult.data.created} created, {dealsUploadResult.data.updated}{' '}
                              updated.
                            </Text>
                          </Box>
                        )}

                        {dealsRowErrors.length > 0 && (
                          <Box bg='red.50' borderRadius='md' p='sm'>
                            <Text fontSize='sm' color='red.600' fontWeight='semibold'>
                              Upload rejected — fix these rows and try again:
                            </Text>
                            {dealsRowErrors.map((msg, i) => (
                              <Text key={i} fontSize='sm' color='red.600'>
                                {msg}
                              </Text>
                            ))}
                          </Box>
                        )}

                        <Box>
                          <Button
                            size='sm'
                            colorScheme='red'
                            leftIcon={<Icon as={FiUpload} />}
                            isLoading={isUploadingDeals}
                            isDisabled={!supplier || !dealsFile}
                            onClick={onUploadDeals}>
                            Upload deals sheet
                          </Button>
                        </Box>
                      </VStack>
                    </VStack>
                  </TabPanel>

                  {/* ---------- Supplier catalog ---------- */}
                  <TabPanel px='0'>
                    <VStack align='stretch' spacing='lg'>
                      {!supplier && (
                        <Text fontSize='sm' color='orange.500'>
                          Select a supplier above to enable the catalog.
                        </Text>
                      )}

                      {/* Download */}
                      <VStack align='stretch' spacing='sm'>
                        <Text fontWeight='semibold'>Download catalog</Text>
                        <Text fontSize='sm' color='gray.500'>
                          Export this supplier&apos;s products. Keep the sheet&apos;s UoM/origin dropdowns intact when
                          editing.
                        </Text>
                        <Box>
                          <Button
                            size='sm'
                            variant='outline'
                            colorScheme='red'
                            leftIcon={<Icon as={FiDownload} />}
                            isLoading={isDownloadingCatalog}
                            isDisabled={!supplier}
                            onClick={() => downloadCatalog()}>
                            Download catalog sheet
                          </Button>
                        </Box>
                      </VStack>

                      <Divider />

                      {/* Upload */}
                      <VStack align='stretch' spacing='sm'>
                        <Text fontWeight='semibold'>Upload catalog</Text>
                        <Text fontSize='sm' color='gray.500'>
                          Create or update products (.xls/.xlsx, max 5&nbsp;MB). A blank ID creates; an existing one
                          updates. Name &amp; UoM required. All-or-nothing.
                        </Text>

                        <FileUploader
                          style='style_01'
                          acceptedTypes={[...EXCEL_MIME_TYPES]}
                          multiple={false}
                          onRemove={() => setCatalogFile(undefined)}
                          onRemoveAll={() => setCatalogFile(undefined)}
                          onUpload={(files: UploadedFile[]) => {
                            setCatalogFile(files[0]);
                            resetCatalogUpload();
                          }}
                        />

                        {catalogUploadResult && (
                          <Box bg='green.50' borderRadius='md' p='sm'>
                            <Text fontSize='sm' color='green.700'>
                              Uploaded: {catalogUploadResult.created} created, {catalogUploadResult.updated} updated
                              {catalogUploadResult.imagesQueued
                                ? `, ${catalogUploadResult.imagesQueued} image(s) queued for processing`
                                : ''}
                              .
                            </Text>
                          </Box>
                        )}

                        {catalogRowErrors.length > 0 && (
                          <Box bg='red.50' borderRadius='md' p='sm'>
                            <Text fontSize='sm' color='red.600' fontWeight='semibold'>
                              Upload rejected — fix these rows and try again:
                            </Text>
                            {catalogRowErrors.map((e, i) => (
                              <Text key={i} fontSize='sm' color='red.600'>
                                Row {e.row} · {e.column}: {e.reason}
                              </Text>
                            ))}
                          </Box>
                        )}

                        <Box>
                          <Button
                            size='sm'
                            colorScheme='red'
                            leftIcon={<Icon as={FiUpload} />}
                            isLoading={isUploadingCatalog}
                            isDisabled={!supplier || !catalogFile}
                            onClick={onUploadCatalog}>
                            Upload catalog sheet
                          </Button>
                        </Box>
                      </VStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant='ghost' colorScheme='gray' onClick={onCloseModal}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
